/**
 * Storefront image URL helpers: Cloudinary + ImageKit transforms.
 * Cloudinary remains primary; ImageKit URLs get ImageKit transforms when present.
 */

const CLOUDINARY_CLOUD =
  process.env.REACT_APP_CLOUDINARY_CLOUD_NAME ||
  process.env.REACT_APP_CLOUDINARY_CLOUD ||
  '';

/** CRA needs REACT_APP_*; also accept IMAGEKIT_URL_ENDPOINT if injected at build. */
const IMAGEKIT_ENDPOINT = (
  process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT ||
  process.env.REACT_APP_IMAGEKIT_ENDPOINT ||
  process.env.IMAGEKIT_URL_ENDPOINT ||
  ''
)
  .trim()
  .replace(/\/+$/, '');

function isCloudinaryUrl(src) {
  return /res\.cloudinary\.com/i.test(String(src || ''));
}

function isImageKitUrl(src) {
  return /ik\.imagekit\.io/i.test(String(src || ''));
}

function buildCloudinaryFetchUrl(url, { width = 420, height = 420, crop = 'pad' } = {}) {
  const src = String(url || '').trim();
  if (!src || !CLOUDINARY_CLOUD || !/^https?:\/\//i.test(src)) return src;
  if (isCloudinaryUrl(src)) return src;
  const w = Math.max(80, Math.round(Number(width) || 420));
  const h = Math.max(80, Math.round(Number(height) || 420));
  const c = String(crop || 'pad');
  const transform = `f_auto,q_auto,w_${w},h_${h},c_${c},b_white`;
  const encoded = encodeURIComponent(src);
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/fetch/${transform}/${encoded}`;
}

export function optimizeCloudinaryUrl(url, { width = 420, height = 420, crop = 'fill' } = {}) {
  const src = String(url || '').trim();
  if (!src || !isCloudinaryUrl(src)) return src;
  if (src.includes('/upload/f_auto') || src.includes('/upload/w_')) return src;

  const w = Math.max(80, Math.round(Number(width) || 420));
  const h = Math.max(80, Math.round(Number(height) || 420));
  const c = String(crop || 'fill');
  const transform = `f_auto,q_auto,w_${w},h_${h},c_${c}`;

  return src.replace('/upload/', `/upload/${transform}/`);
}

/**
 * Apply ImageKit transforms via query string (safe to re-apply if tr= already present — skip).
 * crop: pad → pad_resize + white bg; limit/fill → at_max / maintain_ratio
 */
export function optimizeImageKitUrl(url, { width = 420, height = 420, crop = 'pad' } = {}) {
  const src = String(url || '').trim();
  if (!src || !isImageKitUrl(src)) return src;
  if (/[?&]tr=/i.test(src) || /\/tr:/i.test(src)) return src;

  const w = Math.max(80, Math.round(Number(width) || 420));
  const h = Math.max(80, Math.round(Number(height) || 420));
  const c = String(crop || 'pad').toLowerCase();

  let tr;
  if (c === 'pad') {
    tr = `w-${w},h-${h},c-pad_resize,bg-FFFFFF,f-auto,q-auto`;
  } else if (c === 'limit') {
    tr = `w-${w},h-${h},c-at_max,f-auto,q-auto`;
  } else {
    tr = `w-${w},h-${h},c-maintain_ratio,f-auto,q-auto`;
  }

  const join = src.includes('?') ? '&' : '?';
  return `${src}${join}tr=${tr}`;
}

export function productCardImageUrl(url) {
  const src = String(url || '').trim();
  if (!src) return src;
  if (isCloudinaryUrl(src)) {
    return optimizeCloudinaryUrl(src, { width: 420, height: 420, crop: 'pad' });
  }
  if (isImageKitUrl(src)) {
    return optimizeImageKitUrl(src, { width: 420, height: 420, crop: 'pad' });
  }
  if (CLOUDINARY_CLOUD && /^https?:\/\//i.test(src)) {
    return buildCloudinaryFetchUrl(src, { width: 420, height: 420, crop: 'pad' });
  }
  return src;
}

export function productDetailImageUrl(url) {
  const src = String(url || '').trim();
  if (!src) return src;
  if (isCloudinaryUrl(src)) {
    return optimizeCloudinaryUrl(src, { width: 960, height: 960, crop: 'limit' });
  }
  if (isImageKitUrl(src)) {
    return optimizeImageKitUrl(src, { width: 960, height: 960, crop: 'limit' });
  }
  if (CLOUDINARY_CLOUD && /^https?:\/\//i.test(src)) {
    return buildCloudinaryFetchUrl(src, { width: 960, height: 960, crop: 'limit' });
  }
  return src;
}

export function getImageKitEndpoint() {
  return IMAGEKIT_ENDPOINT;
}
